import {
  calculateInvoiceTotals,
  CREDIT_PACKS,
  VAT_RATE_PERCENT,
  creditPacks,
  type CreditPack,
  type PaymentState,
} from "@zworks/console-contracts";

export type { CreditPack, PaymentState };
export { creditPacks, VAT_RATE_PERCENT };

/** Labels every payment state, so colour never carries the meaning alone. */
export const paymentStateBadges: Record<
  PaymentState,
  {
    label: string;
    variant: "secondary" | "success" | "danger" | "warning" | "unknown" | "outline";
  }
> = {
  pending: { label: "Pending", variant: "secondary" },
  "needs-authentication": { label: "Authentication required", variant: "warning" },
  confirmed: { label: "Confirmed", variant: "success" },
  declined: { label: "Declined", variant: "danger" },
  unknown: { label: "Unknown", variant: "unknown" },
  refunded: { label: "Refunded", variant: "outline" },
  "member-view": { label: "Member view", variant: "outline" },
};

/** A money amount in USD, from integer cents. */
export function formatUsd(cents: number): string {
  return `$${(cents / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** The VAT label for a configured rate, or a no-VAT label when unset or zero. */
export function taxLabel(taxRate: number | null | undefined): string {
  if (taxRate === null || taxRate === undefined) return "Tax, no VAT";
  if (taxRate <= 0) return "Tax, no VAT";
  const percent = Math.round(taxRate * 100 * 100) / 100;
  return `Tax, VAT ${percent.toLocaleString("en-GB", { maximumFractionDigits: 2 })}%`;
}

/** The order summary for a pack: credits, price, VAT, and total. */
export function checkoutOrder(
  pack: CreditPack,
  taxRate: number | null = VAT_RATE_PERCENT / 100,
): {
  credits: number;
  priceCents: number;
  vatCents: number;
  totalCents: number;
} {
  const priced = CREDIT_PACKS[pack];
  const totals = calculateInvoiceTotals(priced.priceCents, taxRate);
  return {
    credits: priced.credits,
    priceCents: totals.subtotalCents,
    vatCents: totals.taxCents,
    totalCents: totals.totalCents,
  };
}
